import { inject, Service } from '@angular/core';
import { toNumber } from 'lodash';
import { EMPTY, Observable } from 'rxjs';
import { first, map, mergeMap, switchMap } from 'rxjs/operators';
import { Api, ApiClient, ApiTypes, EndpointDescriptor, ResultOf } from '@app/api';
import { FoodbackAccountContext } from '@core/classes/account/account-context.class';
import {
  AnalyticsProvider,
  AnalyticsRequestPayload,
} from '@core/interfaces/analytics-payload.interface';
import { PayloadForFoodbacks } from '@core/interfaces/payload-for-foodbacks.interface';
import { AccountService } from '@core/providers/account/account.service';
import { UtilsService } from '@core/utils/utils.service';
import { DateGroupPickerValue } from '@shared/components/date-range-picker';
import {
  DayOfWeekScores,
  HourRangeScores,
  IVenueScores,
  QuestionDetails,
  QuestionFoodbacksFromBackend,
  QuestionImportanceRank,
  QuestionSummary,
  QuestionSummaryEntry,
  VenueCategoryQuestions,
  VenueCategoryScoresDistribution,
  VenueCategorySummary,
  VenueCategorySummaryEntry,
  VenueScore,
  VenueStatistics,
} from '@shared/interfaces/analytics.interface';
import { DynamicDateRange } from '@shared/interfaces/date-range.interface';
import { toVenueStatementFoodbacksQuery } from './analytics/analytics-list-request';
import { CategoriesBackendService } from './categories-backend.service';

/**
 * Every analytics read exists once per context, and the two are not the same call: the account one
 * POSTs the filter as a body, the venue one GETs it as a query. Callers name both and the subclass
 * in play picks its own — which is why the pair is passed around rather than a path.
 */
export interface AnalyticsEndpointPair {
  account: EndpointDescriptor;
  venue: EndpointDescriptor;
}

export type AnalyticsResult<E extends AnalyticsEndpointPair> =
  ResultOf<E['venue']> | ResultOf<E['account'], 'post'>;

export type PlainReadResult<E extends AnalyticsEndpointPair> =
  ResultOf<E['venue']> | ResultOf<E['account']>;

/**
 * The account and venue halves of every shared analytics read, named once so each method points at
 * generated endpoints rather than assembling a path.
 */
const ANALYTICS_ENDPOINTS = {
  statistics: {
    account: Api.surveys.analytics.accounts.statistics,
    venue: Api.surveys.analytics.venues.statistics,
  },
  scores: {
    account: Api.surveys.analytics.accounts.scores,
    venue: Api.surveys.analytics.venues.scores,
  },
  categoryScores: {
    account: Api.surveys.analytics.accounts.category.scores,
    venue: Api.surveys.analytics.venues.category.scores,
  },
  categorySummary: {
    account: Api.surveys.analytics.accounts.category.summary,
    venue: Api.surveys.analytics.venues.category.summary,
  },
  categoryQuestions: {
    account: Api.surveys.analytics.accounts.category.questions,
    venue: Api.surveys.analytics.venues.category.questions,
  },
  categoryScoresDistribution: {
    account: Api.surveys.analytics.accounts.category.distributions.scores,
    venue: Api.surveys.analytics.venues.category.distributions.scores,
  },
  categoryDayOfWeek: {
    account: Api.surveys.analytics.accounts.category.scores.distributions.dayOfWeek,
    venue: Api.surveys.analytics.venues.category.scores.distributions.dayOfWeek,
  },
  categoryHourRanges: {
    account: Api.surveys.analytics.accounts.category.scores.distributions.hourRanges,
    venue: Api.surveys.analytics.venues.category.scores.distributions.hourRanges,
  },
  statementScores: {
    account: Api.surveys.analytics.accounts.statements.scores,
    venue: Api.surveys.analytics.venues.statements.scores,
  },
  statementSummary: {
    account: Api.surveys.analytics.accounts.statements.summary,
    venue: Api.surveys.analytics.venues.statements.summary,
  },
  statementScoresDistribution: {
    account: Api.surveys.analytics.accounts.statements.distributions.scores,
    venue: Api.surveys.analytics.venues.statements.distributions.scores,
  },
  statementDayOfWeek: {
    account: Api.surveys.analytics.accounts.statements.scores.distributions.dayOfWeek,
    venue: Api.surveys.analytics.venues.statements.scores.distributions.dayOfWeek,
  },
  statementHourRanges: {
    account: Api.surveys.analytics.accounts.statements.scores.distributions.hourRanges,
    venue: Api.surveys.analytics.venues.statements.scores.distributions.hourRanges,
  },
  firstFoodbackDate: {
    account: Api.surveys.analytics.accounts.firstFoodbackDate,
    venue: Api.surveys.analytics.venues.firstFoodbackDate,
  },
  categoryAverages: {
    account: Api.surveys.analytics.accounts.categoryAverages,
    venue: Api.surveys.analytics.venues.categoryAverages,
  },
  statementsPerformance: {
    account: Api.surveys.analytics.accounts.statements.performance,
    venue: Api.surveys.analytics.venues.statements.performance,
  },
} as const satisfies Record<string, AnalyticsEndpointPair>;

@Service()
export class SharedAnalyticsBackendService implements AnalyticsProvider {
  private accountService = inject(AccountService);
  protected api = inject(ApiClient);
  protected categoriesBackendService = inject(CategoriesBackendService);
  protected utilsService = inject(UtilsService);

  getStatistics$(
    uuid: string,
    dateRange: DynamicDateRange,
    selectedGroupsUuids?: string[]
  ): Observable<VenueStatistics> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.statistics,
      uuid,
      dateRange,
      selectedGroupsUuids
    );
  }

  getScores$(
    uuid: string,
    dateRange: DynamicDateRange,
    selectedGroupsUuids?: string[]
  ): Observable<IVenueScores> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.scores,
      uuid,
      dateRange,
      selectedGroupsUuids
    ).pipe(map(response => this.mapScoresFromBackendToScores(response)));
  }

  getDayByDayScoresForStatement$(
    uuid: string,
    dateRange: DynamicDateRange,
    statementUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<DayOfWeekScores> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      switchMap((data: FoodbackAccountContext) =>
        data.hasAccessTo().dailyPerformanceOverview
          ? this.makeAnalyticsRequest$(
              ANALYTICS_ENDPOINTS.statementDayOfWeek,
              uuid,
              dateRange,
              selectedGroupsUuids,
              { statement: statementUuid }
            )
          : EMPTY
      )
    );
  }

  getDayByDayScoresForCategory$(
    uuid: string,
    dateRange: DynamicDateRange,
    categoryUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<DayOfWeekScores> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      switchMap((data: FoodbackAccountContext) =>
        data.hasAccessTo().dailyPerformanceOverview
          ? this.makeAnalyticsRequest$(
              ANALYTICS_ENDPOINTS.categoryDayOfWeek,
              uuid,
              dateRange,
              selectedGroupsUuids,
              { category: categoryUuid }
            )
          : EMPTY
      )
    );
  }

  getHourByHourScoresForStatement$(
    uuid: string,
    dateRange: DynamicDateRange,
    statementUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<HourRangeScores> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      switchMap((data: FoodbackAccountContext) =>
        data.hasAccessTo().hourlyPerformanceOverview
          ? this.makeAnalyticsRequest$(
              ANALYTICS_ENDPOINTS.statementHourRanges,
              uuid,
              dateRange,
              selectedGroupsUuids,
              { statement: statementUuid }
            )
          : EMPTY
      )
    );
  }

  getHourByHourScoresForCategory$(
    uuid: string,
    dateRange: DynamicDateRange,
    categoryUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<HourRangeScores> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      switchMap((data: FoodbackAccountContext) =>
        data.hasAccessTo().hourlyPerformanceOverview
          ? this.makeAnalyticsRequest$(
              ANALYTICS_ENDPOINTS.categoryHourRanges,
              uuid,
              dateRange,
              selectedGroupsUuids,
              { category: categoryUuid }
            )
          : EMPTY
      )
    );
  }

  getCategoryScores$(
    uuid: string,
    dateRange: DynamicDateRange,
    categoryUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<IVenueScores> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.categoryScores,
      uuid,
      dateRange,
      selectedGroupsUuids,
      { category: categoryUuid }
    ).pipe(map(response => this.mapScoresFromBackendToScores(response)));
  }

  getOverallExperienceScores$(
    uuid: string,
    contextType: string,
    dateRange: DynamicDateRange,
    selectedGroupsUuids?: string[]
  ): Observable<IVenueScores> {
    return this.categoriesBackendService
      .getOverallExperienceDetails(uuid, contextType, selectedGroupsUuids)
      .pipe(
        mergeMap(overallExperience =>
          overallExperience
            ? this.getCategoryScores$(uuid, dateRange, overallExperience.uuid, selectedGroupsUuids)
            : EMPTY
        )
      );
  }

  getDayByDayOverallExperienceScores$(
    uuid: string,
    contextType: string,
    dateRange: DynamicDateRange,
    selectedGroupsUuids?: string[]
  ): Observable<DayOfWeekScores> {
    return this.categoriesBackendService
      .getOverallExperienceDetails(uuid, contextType, selectedGroupsUuids)
      .pipe(
        mergeMap(overallExperience =>
          overallExperience
            ? this.getDayByDayScoresForCategory$(
                uuid,
                dateRange,
                overallExperience.uuid,
                selectedGroupsUuids
              )
            : EMPTY
        )
      );
  }

  getHourByHourOverallExperienceScores$(
    uuid: string,
    contextType: string,
    dateRange: DynamicDateRange,
    selectedGroupsUuids?: string[]
  ): Observable<HourRangeScores> {
    return this.categoriesBackendService
      .getOverallExperienceDetails(uuid, contextType, selectedGroupsUuids)
      .pipe(
        mergeMap(overallExperience =>
          overallExperience
            ? this.getHourByHourScoresForCategory$(
                uuid,
                dateRange,
                overallExperience.uuid,
                selectedGroupsUuids
              )
            : EMPTY
        )
      );
  }

  getCategorySummary$(
    uuid: string,
    dateRange: DynamicDateRange,
    categoryUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<VenueCategorySummary> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.categorySummary,
      uuid,
      dateRange,
      selectedGroupsUuids,
      { category: categoryUuid }
    ).pipe(
      map((response): VenueCategorySummary => ({
        previous: toCategorySummaryEntry(response.previous),
        current: toCategorySummaryEntry(response.current),
      }))
    );
  }

  getCategoryQuestionsScores$(
    uuid: string,
    dateRange: DynamicDateRange,
    categoryUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<VenueCategoryQuestions> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.categoryQuestions,
      uuid,
      dateRange,
      selectedGroupsUuids,
      { category: categoryUuid }
    );
  }

  getCategoryScoresDistribution$(
    uuid: string,
    dateRange: DynamicDateRange,
    categoryUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<VenueCategoryScoresDistribution> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.categoryScoresDistribution,
      uuid,
      dateRange,
      selectedGroupsUuids,
      { category: categoryUuid }
    );
  }

  getQuestionScoresDistribution$(
    uuid: string,
    dateRange: DynamicDateRange,
    questionUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<VenueCategoryScoresDistribution> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.statementScoresDistribution,
      uuid,
      dateRange,
      selectedGroupsUuids,
      { statement: questionUuid }
    );
  }

  getQuestionSummary$(
    uuid: string,
    dateRange: DynamicDateRange,
    questionUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<QuestionSummary> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.statementSummary,
      uuid,
      dateRange,
      selectedGroupsUuids,
      { statement: questionUuid }
    ).pipe(
      map((response): QuestionSummary => ({
        previous: toQuestionSummaryEntry(response.previous),
        current: toQuestionSummaryEntry(response.current),
      }))
    );
  }

  getQuestionScores$(
    uuid: string,
    dateRange: DynamicDateRange,
    questionUuid: string,
    selectedGroupsUuids?: string[]
  ): Observable<IVenueScores> {
    return this.makeAnalyticsRequest$(
      ANALYTICS_ENDPOINTS.statementScores,
      uuid,
      dateRange,
      selectedGroupsUuids,
      { statement: questionUuid }
    ).pipe(map(response => this.mapScoresFromBackendToScores(response)));
  }

  getFoodbackFirstDate$(uuid: string): Observable<ApiTypes.FirstFoodbackDateResponseDto> {
    return this.plainRead$(ANALYTICS_ENDPOINTS.firstFoodbackDate, uuid);
  }

  getFoodbacksWithQuestion$(
    uuid: string,
    payload: PayloadForFoodbacks,
    questionUuid: string
  ): Observable<QuestionFoodbacksFromBackend> {
    return this.api
      .get(Api.surveys.analytics.venues.statements.foodbacks.paged, {
        pathParams: { venueUuid: uuid, statement: questionUuid },
        query: toVenueStatementFoodbacksQuery(payload),
      })
      .pipe(map(response => ({ totalCount: response.meta.total, items: response.items })));
  }

  getQuestionDetails$(questionUuid: string): Observable<QuestionDetails> {
    return this.api.get(Api.surveys.statements, { pathParams: { uuid: questionUuid } });
  }

  getQuestionImportanceRankAccountLevel$(
    questionUuid: string,
    accountUuid: string,
    dateFrom: string,
    dateTo: string
  ): Observable<QuestionImportanceRank> {
    return this.api.post(Api.surveys.analytics.accounts.statements.importanceRank, {
      pathParams: { accountUuid, statement: questionUuid },
      body: { dateFrom, dateTo },
    });
  }

  getQuestionImportanceRankVenueLevel$(
    questionUuid: string,
    venueUuid: string,
    dateFrom: string,
    dateTo: string
  ): Observable<QuestionImportanceRank> {
    return this.api.get(Api.surveys.analytics.venues.statements.importanceRank, {
      pathParams: { venueUuid, statement: questionUuid },
      query: { dateFrom, dateTo },
    });
  }

  /**
   * The venue form of a filtered read: everything travels in the query string. The account service
   * overrides this because its half of the pair takes the filter as a POST body instead.
   */
  protected makeAnalyticsRequest$<E extends AnalyticsEndpointPair>(
    endpoints: E,
    uuid: string,
    dateRange: DynamicDateRange,
    selectedGroupsUuids?: string[],
    extraPathParams: Record<string, string> = {}
  ): Observable<AnalyticsResult<E>> {
    const endpoint: EndpointDescriptor = endpoints.venue;

    return this.api.get(endpoint, {
      pathParams: { venueUuid: uuid, ...extraPathParams },
      query: this.getRequestParams(dateRange, selectedGroupsUuids),
    }) as Observable<AnalyticsResult<E>>;
  }

  /** A read that carries no filter at all, so the two contexts differ only in the path param. */
  protected plainRead$<E extends AnalyticsEndpointPair>(
    endpoints: E,
    uuid: string,
    extraPathParams: Record<string, string> = {}
  ): Observable<PlainReadResult<E>> {
    const endpoint: EndpointDescriptor = endpoints.venue;

    return this.api.get(endpoint, {
      pathParams: { venueUuid: uuid, ...extraPathParams },
    }) as Observable<PlainReadResult<E>>;
  }

  protected getRequestParams(
    dateRange: DynamicDateRange,
    selectedGroupsUuids: string[] = []
  ): AnalyticsRequestPayload {
    return {
      dateFrom: this.utilsService.prepareDateForRequest(dateRange.start),
      dateTo: this.utilsService.prepareDateForRequest(dateRange.end),
      precision: DateGroupPickerValue.CUSTOM,
      'venueGroups[]': selectedGroupsUuids,
    };
  }

  private mapScoresFromBackendToScores(scores: ApiTypes.ScoresTrendResponseDto): IVenueScores {
    return {
      currentCount: scores.currentCount.slice(),
      currentScores: scores.currentScores.map(this.mapScoreEntryFromBackendToScore),
      previousScores: scores.previousScores.map(this.mapScoreEntryFromBackendToScore),
    };
  }

  private mapScoreEntryFromBackendToScore(score: ApiTypes.ScoreTrendPointResponseDto): VenueScore {
    return {
      date: score.date,
      value: toNumber(score.value),
    };
  }
}

/** The backend sends null when nobody answered in the period; lodash's `toNumber` reads that as 0. */
function averageScore(value: string | null): number | null {
  return value === null ? null : toNumber(value);
}

function toCategorySummaryEntry(
  entry: ApiTypes.CategorySummaryStatsResponseDto
): VenueCategorySummaryEntry {
  return {
    foodbacksAverageScore: averageScore(entry.foodbacksAverageScore),
    foodbacksCount: entry.foodbacksCount,
    commentsCount: entry.commentsCount,
    totalFoodbacksCount: entry.totalFoodbacksCount,
    totalCommentsCount: entry.totalCommentsCount,
  };
}

function toQuestionSummaryEntry(
  entry: ApiTypes.StatementSummaryStatsResponseDto
): QuestionSummaryEntry {
  return {
    foodbacksAverageScore: averageScore(entry.foodbacksAverageScore),
    foodbacksCount: entry.foodbacksCount,
    commentsCount: entry.commentsCount,
    totalFoodbacksCount: entry.totalFoodbacksCount,
    totalCommentsCount: entry.totalCommentsCount,
  };
}
